import { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Card,
  CardContent,
  Avatar,
  Divider,
  IconButton,
  Snackbar,
  Alert,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import AutoModeIcon from "@mui/icons-material/AutoMode";

export function ReviewModal({
  isOpen,
  onClose,
  ephTotal,
  ephSelected,
  ephAutoSelected = 0,
  vendorTotal,
  vendorSelected,
  vendorAutoSelected = 0,
  notFoundTotal,
  notFoundSelected,
  isVendorCountrySupported = true,
  onFinalSubmit,
}) {
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [toast, setToast] = useState({ open: false, message: "", severity: "info" });

  const totalSelected =
    ephSelected + (isVendorCountrySupported ? vendorSelected : 0) + notFoundSelected;

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setToast({ open: true, message: "Your data is submitting...", severity: "info" });
    try {
      await onFinalSubmit();
      setToast({ open: true, message: "Data submitted successfully!", severity: "success" });
      setShowConfirmation(true);
    } catch (error) {
      setToast({ open: true, message: "Something went wrong, try again", severity: "error" });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setShowConfirmation(false);
    onClose();
  };

  const handleToastClose = () => {
    setToast((prev) => ({ ...prev, open: false }));
  };

  return (
    <>
      <Dialog
        open={isOpen}
        // onClose={handleClose}
        onClose={() => {}}
        maxWidth="sm"
        fullWidth
        className="review-modal-container"
      >
        <div className="diglog-div-cs">
          <DialogTitle
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography variant="h6">Review Your Selection</Typography>
            <IconButton onClick={handleClose} size="small">
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Please review your selections before submitting
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {/* EPH Tab Summary */}
              <Card className="card-eph">
                <CardContent
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    py: 2,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Avatar className="card-eph-logo-cs">
                      <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                        EPH
                      </Typography>
                    </Avatar>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: "medium" }}>
                        EPH Records
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Enterprise Profile Hub
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ textAlign: "right" }}>
                    <Typography
                      variant="h6"
                      color="primary.main"
                      sx={{ fontWeight: "bold", display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 0.5 }}
                    >
                      {ephSelected}
                      {ephAutoSelected > 0 && (
                        <Tooltip title="Auto Selected Results" arrow>
                          <Typography
                            component="span"
                            variant="caption"
                            sx={{ fontWeight: "bold", display: "inline-flex", alignItems: "center", gap: 0.3, color: "green" }}
                          >
                            (<AutoModeIcon sx={{ fontSize: "14px" }} /> {ephAutoSelected})
                          </Typography>
                        </Tooltip>
                      )}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Selected out of {ephTotal}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>

              {/* Vendor Tab Summary */}
              {isVendorCountrySupported && (
                <Card className="card-vendor-select-cs">
                  <CardContent
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      py: 2,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Avatar className="card-cs-logo-vendor">
                        <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                          VEN
                        </Typography>
                      </Avatar>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: "medium" }}>
                          Vendor Records
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Vendor data
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ textAlign: "right" }}>
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: "bold", display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 0.5, color: "#f09a1a" }}
                      >
                        {vendorSelected}
                        {vendorAutoSelected > 0 && (
                          <Tooltip title="Auto Selected Results" arrow>
                            <Typography
                              component="span"
                              variant="caption"
                              sx={{ fontWeight: "bold", display: "inline-flex", alignItems: "center", gap: 0.3, color: "green" }}
                            >
                              (<AutoModeIcon sx={{ fontSize: "14px" }} /> {vendorAutoSelected})
                            </Typography>
                          </Tooltip>
                        )}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Selected out of {vendorTotal}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              )}

              {/* Not Found Tab Summary */}
              <Card className="card-not-found-cs">
                <CardContent
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    py: 2,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Avatar className="card-not-found-logo-cs">
                      <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                        N/F
                      </Typography>
                    </Avatar>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: "medium" }}>
                        Not Found Records
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Missing data
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ textAlign: "right" }}>
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: "bold", color: "#dc2626" }}
                    >
                      {notFoundSelected}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Selected out of {notFoundTotal}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>

              {/* Total Summary */}
              <Box>
                <Divider sx={{ mb: 1 }} />
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography variant="body1" sx={{ fontWeight: "medium" }}>
                    Total Selected
                  </Typography>
                  <Typography
                    variant="h4"
                    color="error"
                    sx={{ fontWeight: "bold", fontSize: "24px" }}
                  >
                    {totalSelected}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </DialogContent>

          <DialogActions
            className="btn-review-cs"
            sx={{ p: 2}}
          >
            <Button
              onClick={handleClose}
              variant="outlined"
              sx={{ textTransform: "none" }}
            >
              Close
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={totalSelected === 0 || isSubmitting}
              variant="contained"
              sx={{ textTransform: "none", fontWeight: 600, minWidth: 100 }}
              color="error"
              startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : null}
            >
              {isSubmitting ? "Submitting..." : "Submit"}
            </Button>
          </DialogActions>
        </div>
      </Dialog>

      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={handleToastClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleToastClose}
          severity={toast.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </>
  );
}